import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { callEdgeFunction } from '../lib/supabase'
import { ErrorAlert } from '../components/ui'

export default function ContactUs() {
  const { t, i18n } = useTranslation('common')
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  })
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const updateField = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    if (!formData.name.trim()) {
      setError(t('contactForm.validationName'))
      return
    }

    if (!formData.email.trim()) {
      setError(t('contactForm.validationEmail'))
      return
    }

    if (!formData.message.trim()) {
      setError(t('contactForm.validationMessage'))
      return
    }

    setLoading(true)
    try {
      await callEdgeFunction('submit-contact', { ...formData, lang: i18n.language })
      setSuccess(true)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5">
        <div className="w-full max-w-md text-center animate-fadeIn">
          <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="font-display text-2xl font-medium text-gray-800 mb-3">
            {t('contactForm.successTitle')}
          </h1>
          <p className="text-gray-600 mb-2">
            {t('contactForm.successMessage')}
          </p>
          <p className="text-gray-500 text-sm mb-6">
            {t('contactForm.successDetail')}
          </p>
          <Link
            to="/"
            className="text-primary-600 font-semibold hover:underline"
          >
            {t('buttons.backToHome')}
          </Link>
        </div>
      </div>
    )
  }

  const inputClass = "w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base transition-all focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100"

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-lg animate-fadeIn">
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl font-medium text-gray-800 mb-2">
            {t('contactForm.title')}
          </h1>
          <p className="text-gray-500">{t('appFullName')}</p>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-sm">
          <ErrorAlert message={error} onDismiss={() => setError(null)} />

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block font-semibold text-gray-800 mb-2">{t('contactForm.nameLabel')}</label>
              <input
                type="text"
                className={inputClass}
                value={formData.name}
                onChange={updateField('name')}
                placeholder={t('contactForm.namePlaceholder')}
                required
                autoFocus
              />
            </div>

            <div>
              <label className="block font-semibold text-gray-800 mb-2">{t('contactForm.emailLabel')}</label>
              <input
                type="email"
                className={inputClass}
                value={formData.email}
                onChange={updateField('email')}
                placeholder={t('contactForm.emailPlaceholder')}
                required
              />
            </div>

            <div>
              <label className="block font-semibold text-gray-800 mb-2">{t('contactForm.messageLabel')}</label>
              <textarea
                className={inputClass + ' resize-none'}
                rows={5}
                value={formData.message}
                onChange={updateField('message')}
                placeholder={t('contactForm.messagePlaceholder')}
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl font-semibold transition-all hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? t('contactForm.sending') : t('contactForm.sendMessage')}
            </button>
          </form>

          <div className="text-center mt-5">
            <Link to="/" className="text-gray-500 text-sm hover:text-gray-700">
              {t('buttons.backToHome')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
